import type { TextInputFocusEvent, TextInputProps } from '../../primitives/TextInput';

/** Every one-time code Flashka sends is six digits (the auth worker's format). */
export const CODE_INPUT_DEFAULT_LENGTH = 6;

export interface CodeInputProps {
  /**
   * Controlled value — digits only, never longer than {@link length}. There is
   * no uncontrolled mode: a code step always owns the digits (it has to clear
   * them when the worker rejects a code, or the enabled CTA would resubmit the
   * same wrong one and burn an attempt).
   */
  value: string;
  /** Fires with the digits typed so far. Non-digits never reach it. */
  onChangeText: (text: string) => void;
  /**
   * Fires ONCE when a change fills the last cell — typed, pasted or
   * autofilled. This is what lets a code step submit itself on the sixth
   * digit without the caller watching `value.length` in an effect.
   */
  onComplete?: (code: string) => void;
  /** Number of cells. Default 6 ({@link CODE_INPUT_DEFAULT_LENGTH}). */
  length?: number;
  /** Dims the group to 50% and blocks editing. */
  disabled?: boolean;
  autoFocus?: boolean;
  onFocus?: (event: TextInputFocusEvent) => void;
  onBlur?: (event: TextInputFocusEvent) => void;
  /**
   * Required, not optional: the cells carry no visible label, so without this
   * the field is announced as an unnamed text box. Pre-translated — the DS
   * carries no i18n.
   */
  accessibilityLabel: string;
  /**
   * E2E id on the group's wrapper (RN `testID` / web `data-testid`). The
   * typeable field (web `input-otp`'s `<input>`, native's transparent
   * overlay) is `<testID>-input`; each drawn cell `<testID>-cell-<index>`
   * (0-based).
   */
  testID?: string;
  /** Extra classes for the group's wrapper. */
  className?: string;
  /** Pass-through to the underlying web `<input>` (name, autoComplete, …). */
  webProps?: TextInputProps['webProps'];
  /** Pass-through to the underlying react-native TextInput. */
  nativeProps?: TextInputProps['nativeProps'];
  /**
   * Native only — swap the underlying react-native `TextInput`, exactly like
   * {@link TextInputProps.nativeInputComponent}. Pass gorhom's
   * `BottomSheetTextInput` when the field lives inside a bottom sheet.
   * Ignored on web.
   */
  nativeInputComponent?: TextInputProps['nativeInputComponent'];
}

export interface CodeInputCellsProps {
  /**
   * One entry per cell — the digit in it, or `null`/`''` when empty. Its
   * length IS the number of cells rendered.
   */
  chars: (string | null)[];
  /** The CodeInput's root id — cells become `<testID>-cell-<index>`. */
  testID?: string;
  /**
   * The cells that read as active, `[start, end)`, or `null` when the field is
   * not focused. One cell (`end = start + 1`) is the caret: where the next
   * digit lands. More than one is a SELECTION (select all, before a copy or a
   * cut), and every selected cell carries the border, the way `input-otp`
   * marks each selected slot active. There is no separate caret (see
   * `CodeInput.variants.ts`).
   */
  active: CodeInputActiveRange | null;
}

/** A run of cells, `start` inclusive and `end` exclusive. */
export interface CodeInputActiveRange {
  start: number;
  end: number;
}
